/**
 * Alle Schlüssel für localStorage und sessionStorage an einer Stelle.
 *
 * Die Zeichenketten dürfen sich nie ändern: Unter `breastfeeding_paid` liegt bei
 * zahlenden Nutzerinnen der Bezahlstatus, unter den Voucher-Schlüsseln ein eingelöster
 * Code. Ein umbenannter Schlüssel würde beides stillschweigend verlieren. Der Test in
 * storageKeys.test.ts schreibt die Literale fest.
 */

// Bezahlung und Gutscheine (localStorage)
export const PAID_STORAGE_KEY = 'breastfeeding_paid'
export const VOUCHER_CODE_KEY = 'breastfeeding_voucher_code'
export const VOUCHER_DISCOUNT_KEY = 'breastfeeding_voucher_discount'

// Hebammen-Anbindung (localStorage)
export const MIDWIFE_CODE_KEY = 'breastfeeding_midwife_code'
export const SKIPPED_MIDWIFE_KEY = 'breastfeeding_skipped_midwife'
export const REFERRAL_ONBOARDING_SEEN_KEY = 'breastfeeding_referral_onboarding_seen'

// Darstellung (localStorage)
export const THEME_KEY = 'theme'
export const WEIGHT_CHART_Y_MIN_KEY = 'tracking-weight-ymin'
export const WEIGHT_CHART_Y_MAX_KEY = 'tracking-weight-ymax'
export const TRACKING_CALENDAR_OPEN_KEY = 'tracking-calendar-open'
export const PAIN_CHART_OPEN_KEY = 'tracking-pain-chart-open'
export const BREAST_SIDE_CHART_OPEN_KEY = 'tracking-breast-side-chart-open'
export const NURSING_DIAPER_CHART_OPEN_KEY = 'tracking-nursing-diaper-chart-open'

// Nur für die laufende Sitzung (sessionStorage)
export const AUTH_ROLE_MISMATCH_KEY = 'authRoleMismatch'
export const CHAPTER_LIST_ORDER_PREFIX = 'chapterListOrder:'
/** Schaltet die Testwerkzeuge im Profil frei, siehe lib/testTools.ts. */
export const TEST_TOOLS_KEY = 'testtools'

/**
 * Zugriff auf localStorage, der im privaten Modus oder bei vollem Speicher nicht wirft,
 * sondern null bzw. false liefert. Für JSON-Werte mit React-State gibt es useLocalStorage.
 */
export function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function writeStorage(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

export function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // Nichts zu tun: Wenn der Speicher nicht erreichbar ist, ist der Wert auch nicht da.
  }
}

/**
 * Alles löschen, was an das angemeldete Konto gebunden ist. Gehört zu jedem Abmelden:
 * `breastfeeding_paid` ist der Startwert der Bezahlschranke, bis die Datenbank
 * antwortet - bleibt er liegen, sieht die nächste Person auf demselben Gerät kurz
 * bezahlte Inhalte. Theme und die Chart-Einstellungen bleiben, die gehören zum Gerät.
 */
export function clearLocalUserState(): void {
  const keys = [
    PAID_STORAGE_KEY,
    VOUCHER_CODE_KEY,
    VOUCHER_DISCOUNT_KEY,
    MIDWIFE_CODE_KEY,
    SKIPPED_MIDWIFE_KEY,
    REFERRAL_ONBOARDING_SEEN_KEY,
    TONGUE_TIE_STATE_KEY,
    TONGUE_TIE_CUT_AT_KEY,
    VITAMIN_K_DOSES_KEY,
  ]
  for (const key of keys) removeStorage(key)
}

/**
 * Die eigenen Notfallnummern: Hebamme, Klinik und Kinderärztin sind bei jeder Frau
 * andere. Sie liegen auf dem Gerät und nicht im Konto - es ist eine Wahlhilfe, kein
 * Befund, und im Notfall darf sie nicht von einer Antwort des Servers abhängen.
 */
export const MIDWIFE_PHONE_KEY = 'tracking-midwife-phone'
export const CLINIC_PHONE_KEY = 'tracking-clinic-phone'
export const PEDIATRICIAN_PHONE_KEY = 'tracking-pediatrician-phone'

/** Gemerkte Antworten des Check-ins, siehe checkin/rememberedAnswers.ts. */
export const TONGUE_TIE_STATE_KEY = 'tracking-tongue-tie-state'
export const TONGUE_TIE_CUT_AT_KEY = 'tracking-tongue-tie-cut-at'
export const VITAMIN_K_DOSES_KEY = 'tracking-vitamin-k-doses'

/** Zuletzt gewählte Herkunft der Muttermilch, siehe checkin/rememberedAnswers.ts. */
export const BOTTLE_MILK_SOURCE_KEY = 'tracking-bottle-milk-source'
